import { useEffect, useRef } from 'react';
import { getUserId, sendPushReminder } from './dbService';

const DAY_MS  = 24 * 60 * 60 * 1000;
const TWO_HRS = 2 * 60 * 60 * 1000;

/** Returns true if the stored timestamp for `key` is older than `windowMs` (or absent).
 *  Fails open (returns true) if localStorage is unavailable — better to send an
 *  extra reminder than to silently never remind. */
function shouldRemindIn(key, windowMs) {
  try {
    const last = localStorage.getItem(key);
    if (!last) return true;
    const ts = parseInt(last, 10);
    if (isNaN(ts)) return true; // corrupted value — treat as unseen
    return Date.now() - ts >= windowMs;
  } catch {
    return true; // fail-open: localStorage unavailable, assume reminder is due
  }
}

/** Shorthand for the default 24-hour window. */
function shouldRemind(key) { return shouldRemindIn(key, DAY_MS); }

function markReminded(key) {
  try { localStorage.setItem(key, String(Date.now())); } catch {}
}

/**
 * usePendingReminders
 * Sends a push notification for:
 *   1. Incoming friend requests (at most once per 24 h)
 *   2. Pending group invitations (at most once per 24 h)
 *   3. Upcoming group exercise sessions — fires exactly `reminderOffset` minutes
 *      before each session. Uses setTimeout so it fires at the right time even if
 *      the app was opened well in advance.
 */
export function usePendingReminders({ friendRequests, currentUserId, lang, reminderOffset = 15 }) {
  const nl       = lang === 'nl';
  const timerIds = useRef([]);

  // Clear scheduled timers on unmount or when deps change
  useEffect(() => {
    return () => {
      timerIds.current.forEach(id => clearTimeout(id));
      timerIds.current = [];
    };
  }, [currentUserId, reminderOffset]);

  useEffect(() => {
    if (!currentUserId) return;

    const run = async () => {
      // ── 1. Incoming friend requests ─────────────────────────────────────
      const incoming = (friendRequests || []).filter(
        r => r.to === currentUserId && r.status === 'pending'
      );
      if (incoming.length > 0 && shouldRemind('sl_push_rm_friends')) {
        const n = incoming.length;
        markReminded('sl_push_rm_friends'); // mark before async to prevent race on re-render
        await sendPushReminder(
          nl ? 'Sense Lifting — Vriendschapsverzoeken' : 'Sense Lifting — Friend Requests',
          nl
            ? `Je hebt ${n} openstaand${n > 1 ? 'e' : ''} vriendschapsverzoek${n > 1 ? 'en' : ''}. Tik om te bekijken.`
            : `You have ${n} pending friend request${n > 1 ? 's' : ''}. Tap to view.`,
          '/'
        );
      }

      // ── 0. Weekly digest — every Sunday ──────────────────────────────────
      if (new Date().getDay() === 0 && shouldRemind('sl_push_rm_digest')) {
        try {
          markReminded('sl_push_rm_digest'); // mark before async to prevent race on re-render
          // Streak: count consecutive days with any activity
          let streak = 0;
          try {
            const sleepLog = JSON.parse(localStorage.getItem('sl_sleep_log') || '[]');
            const moodLog  = JSON.parse(localStorage.getItem('sl_mood_log') || '[]');
            const exHist   = JSON.parse(localStorage.getItem('sl_ex_history') || '[]');
            const today    = new Date(); today.setHours(0,0,0,0);
            for (let d = 0; d < 7; d++) {
              const dayStart = today.getTime() - d * 86400000;
              const dayEnd   = dayStart + 86400000;
              const active   = sleepLog.some(e => e.date >= dayStart && e.date < dayEnd)
                || moodLog.some(e => e.date >= dayStart && e.date < dayEnd)
                || exHist.some(e => e.date >= dayStart && e.date < dayEnd);
              if (active) streak++; else if (d > 0) break;
            }
          } catch {}
          const digestTitle = nl ? 'Sense Lifting — Weekoverzicht' : 'Sense Lifting — Weekly Digest';
          const digestBody  = streak > 0
            ? (nl
                ? `Geweldig werk deze week! Je huidige streak is ${streak} dag${streak !== 1 ? 'en' : ''}. Tik om je voortgang te bekijken.`
                : `Great work this week! Your current streak is ${streak} day${streak !== 1 ? 's' : ''}. Tap to see your progress.`)
            : (nl
                ? 'Een nieuw begin van de week. Log je slaap en stemming om je gereedheid bij te houden!'
                : 'A fresh week starts today. Log sleep and mood to track your readiness!');
          await sendPushReminder(digestTitle, digestBody, '/');
        } catch {}
      }

      // ── 2. Pending group invitations ─────────────────────────────────────
      // Two separate keys:
      //   sl_push_rm_groups_check  — throttles the API fetch (2 h) to avoid hammering
      //                              the endpoint on every render when no invites exist.
      //   sl_push_rm_groups        — throttles the actual push (24 h) so the user isn't
      //                              notified more than once per day.
      if (shouldRemindIn('sl_push_rm_groups_check', TWO_HRS)) {
        markReminded('sl_push_rm_groups_check'); // always mark check, regardless of result
        try {
          const BASE = process.env.REACT_APP_API_URL || '/api';
          const res  = await fetch(`${BASE}/groups?uid=${encodeURIComponent(currentUserId)}`);
          if (res.ok) {
            const groups        = await res.json();
            const pendingGroups = (groups || []).filter(g => g.myStatus === 'pending');
            if (pendingGroups.length > 0 && shouldRemind('sl_push_rm_groups')) {
              const n = pendingGroups.length;
              markReminded('sl_push_rm_groups'); // mark before async to prevent race on re-render
              await sendPushReminder(
                nl ? 'Sense Lifting — Groepsuitnodigingen' : 'Sense Lifting — Group Invitations',
                nl
                  ? `Je bent uitgenodigd voor ${n} groep${n > 1 ? 'en' : ''}. Tik om te bekijken.`
                  : `You've been invited to ${n} group${n > 1 ? 's' : ''}. Tap to view.`,
                '/'
              );
            }
          }
        } catch {}
      }

      // ── 2b. Trial end reminder (day 8+, freemium only, once) ─────────────
      try {
        const licenseRaw  = localStorage.getItem('sl_license');
        const curLicense  = licenseRaw ? JSON.parse(licenseRaw) : 'freemium';
        const signedUpRaw = localStorage.getItem('sl_signed_up');
        const signedUpVal = signedUpRaw ? JSON.parse(signedUpRaw) : null;
        if (curLicense === 'freemium' && signedUpVal) {
          const daysSince = (Date.now() - new Date(signedUpVal).getTime()) / 86400000;
          if (daysSince >= 7 && shouldRemind('sl_push_rm_trial_end')) {
            const daysLeft = Math.max(0, 10 - Math.floor(daysSince));
            markReminded('sl_push_rm_trial_end');
            await sendPushReminder(
              nl ? 'Sense Lifting — Upgrade vereist' : 'Sense Lifting — Upgrade required',
              nl
                ? (daysLeft > 0
                    ? `Je gratis periode is voorbij. Nog ${daysLeft} dag${daysLeft !== 1 ? 'en' : ''} voor je account wordt verwijderd. Upgrade nu!`
                    : 'Je gratis periode is voorbij. Upgrade nu om je account te bewaren.')
                : (daysLeft > 0
                    ? `Your free trial has ended. ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left before account deletion. Upgrade now!`
                    : 'Your free trial has ended. Upgrade now to keep your account.'),
              '/'
            );
          }
        }
      } catch {}

      // ── 3. Upcoming group exercise sessions ──────────────────────────────
      try {
        const BASE       = process.env.REACT_APP_API_URL || '/api';
        const uid        = getUserId();
        const offsetMs   = (reminderOffset || 15) * 60 * 1000;
        // Fetch sessions within next 48 h to cover all possible reminder windows
        const res        = await fetch(`${BASE}/groups/upcoming-sessions?uid=${encodeURIComponent(uid)}`);
        if (!res.ok) return;
        const sessions   = await res.json();
        if (!Array.isArray(sessions)) return;

        const now = Date.now();

        for (const se of sessions) {
          const key         = `sl_push_rm_session_${se.id}`;
          if (!shouldRemind(key)) continue;

          const sessionTime = new Date(se.scheduledAt).getTime();
          if (isNaN(sessionTime)) continue;
          if (now >= sessionTime) continue; // session already started

          const reminderAt  = sessionTime - offsetMs;

          const fireReminder = async () => {
            // Re-check guard in case the timer fired after a re-mount
            if (!shouldRemind(key)) return;
            const diffMs  = sessionTime - Date.now();
            if (diffMs < 0) return; // session already past
            const diffMin = Math.round(diffMs / 60000);
            const diffH   = Math.round(diffMs / 3600000);
            const timeStr = new Date(sessionTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const dayStr  = diffMin < 2
              ? (nl ? 'nu' : 'now')
              : diffMin < 120
              ? (nl ? `over ${diffMin} minuten` : `in ${diffMin} minutes`)
              : diffH < 24
              ? (nl ? `om ${timeStr}` : `at ${timeStr}`)
              : (nl ? `morgen om ${timeStr}` : `tomorrow at ${timeStr}`);

            markReminded(key); // mark before async to prevent race on re-mount
            await sendPushReminder(
              nl
                ? `Sense Lifting — Groepsoefening: ${se.exName}`
                : `Sense Lifting — Group session: ${se.exName}`,
              nl
                ? `${se.groupName}: "${se.exName}" is gepland ${dayStr}. Tik om deel te nemen.`
                : `${se.groupName}: "${se.exName}" is scheduled ${dayStr}. Tap to join.`,
              '/'
            );
          };

          if (reminderAt <= now) {
            // Reminder time already passed — fire immediately
            await fireReminder();
          } else {
            // Schedule for the exact right moment
            const delay = reminderAt - now;
            const tid   = setTimeout(fireReminder, delay);
            timerIds.current.push(tid);
          }
        }
      } catch {}
    };

    run();
  }, [currentUserId, nl, reminderOffset]); // eslint-disable-line react-hooks/exhaustive-deps
}
